import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData, useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface FeedFormData {
  pond_id: string;
  feed_date: string;
  quantity_kg: number;
  feed_brand: string;
  feed_type?: string;
  remarks?: string;
  location_id?: string;
}

interface FeedLog {
  id: string;
  pond_id: string;
  feed_date: string;
  quantity_kg: number;
  feed_brand: string;
  feed_type?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface PondFeedData {
  id: string;
  name: string;
  pond_type: string;
  quantity_kg: string;
  feed_brand: string;
  feed_type: string;
  remarks: string;
  expanded: boolean;
}

const FeedForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [feedDate, setFeedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [pondsData, setPondsData] = useState<PondFeedData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const { data: feedBrands } = useMasterData('feed_brands', selectedLocation?.id || null);
  const [inventoryBalances, setInventoryBalances] = useState<any[]>([]);
  const { mutate: createFeedLog, loading: creating } = useMutation<FeedLog, FeedFormData>();

  // Load inventory balances for selected location
  useEffect(() => {
    const loadBalances = async () => {
      if (!selectedLocation) {
        setInventoryBalances([]);
        return;
      }
      try {
        const res = await apiClient.get('/inventory/balances', {
          params: { location_id: selectedLocation.id },
        });
        setInventoryBalances(res.data || []);
      } catch {
        setInventoryBalances([]);
      }
    };
    loadBalances();
  }, [selectedLocation]);

  // Build a quick lookup for inventory on hand by brand name
  const feedBrandStockByName: Record<string, number> = React.useMemo(() => {
    const stock: Record<string, number> = {};
    (inventoryBalances || []).forEach((b: any) => {
      if (b?.item_name) stock[b.item_name] = b.on_hand_quantity || 0;
    });
    return stock;
  }, [inventoryBalances]);

  // Show all feed brands from master data; annotate with stock info
  const availableFeedBrands = (feedBrands || []) as any[];

  useEffect(() => {
    if (ponds && ponds.length > 0) {
      setPondsData(ponds.map(pond => ({
        id: pond.id,
        name: pond.name,
        pond_type: pond.pond_type,
        quantity_kg: '',
        feed_brand: '',
        feed_type: '',
        remarks: '',
        expanded: false
      })));
    } else if (selectedLocation && (!ponds || ponds.length === 0)) {
      // No ponds available for this location
      setPondsData([]);
    }
  }, [ponds, selectedLocation]);

  const updatePond = (pondId: string, field: keyof PondFeedData, value: string | boolean) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, [field]: value } : pond
    ));
  };

  const togglePondExpansion = (pondId: string) => {
    setPondsData(pondsData.map(pond => 
      pond.id === pondId ? { ...pond, expanded: !pond.expanded } : pond
    ));
  };

  const handleVoiceInput = (pondId: string) => {
    alert(`Voice input for ${pondId} - Feature would integrate with device microphone`);
  };

  // Bulk actions for efficiency
  const copyToAllPonds = (field: keyof PondFeedData, value: string) => {
    setPondsData(pondsData.map(pond => ({ ...pond, [field]: value })));
  };

  const clearAllPonds = () => {
    setPondsData(pondsData.map(pond => ({ 
      ...pond, 
      quantity_kg: '', 
      feed_brand: '', 
      feed_type: '',
      remarks: '' 
    })));
  };

  const handleSubmit = async () => {
    const validEntries = pondsData.filter(pond => pond.quantity_kg && pond.feed_brand);
    
    if (validEntries.length === 0) {
      alert('Please fill feed data for at least one pond');
      return;
    }

    try {
      setLoading(true);
      const promises = validEntries.map(pond => 
        createFeedLog('/feed-logs', {
          pond_id: pond.id,
          feed_date: feedDate,
          quantity_kg: parseFloat(pond.quantity_kg),
          feed_brand: pond.feed_brand,
          feed_type: pond.feed_type || undefined,
          remarks: pond.remarks || undefined,
          location_id: selectedLocation?.id,
        })
      );
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded feed data for ${validEntries.length} ponds on ${feedDate}`);
      
      // Reset form
      setPondsData(pondsData.map(pond => ({ 
        ...pond, 
        quantity_kg: '', 
        feed_brand: '', 
        feed_type: '',
        remarks: '' 
      })));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalQuantity = () => {
    return pondsData.reduce((sum, pond) => sum + (parseFloat(pond.quantity_kg) || 0), 0).toFixed(1);
  };

  const getFilledPondsCount = () => {
    return pondsData.filter(pond => pond.quantity_kg && pond.feed_brand).length;
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-10">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">🌾 Feed Log - EOD</h1>
        </div>
        
        {/* Date Selection */}
        <div className="flex items-center gap-2 mb-3">
          <input
            type="date"
            value={feedDate}
            onChange={(e) => setFeedDate(e.target.value)}
            className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={clearAllPonds}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
          >
            <span className="text-xs">🔄</span>
            Clear All
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="bg-blue-50 p-2 rounded-lg">
          <div className="flex justify-between text-xs text-blue-700 mb-1">
            <span>Progress</span>
            <span>{getFilledPondsCount()}/{pondsData.length} ponds completed</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(getFilledPondsCount() / pondsData.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Ponds List */}
      <div className="p-4 space-y-3">
        {pondsData.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🌾</div>
            <p className="text-gray-500 mb-2">No ponds available</p>
            <p className="text-sm text-gray-400">Please make sure you have selected a location and have ponds configured.</p>
          </div>
        ) : (
          pondsData.map((pond) => (
          <div key={pond.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Pond Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{pond.name}</h3>
                  <p className="text-sm text-gray-500">{pond.pond_type === 'nursery' ? 'Nursery' : 'Grower'}</p>
                </div>
                <button
                  onClick={() => togglePondExpansion(pond.id)}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  {pond.expanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Entry Fields */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Quantity (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={pond.quantity_kg}
                    onChange={(e) => updatePond(pond.id, 'quantity_kg', e.target.value)}
                    placeholder="0.0"
                    className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Feed Brand
                  </label>
                  <select
                    value={pond.feed_brand}
                    onChange={(e) => updatePond(pond.id, 'feed_brand', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Brand</option>
                    {availableFeedBrands?.map((brand) => {
                      const stock = feedBrandStockByName[brand.name] ?? 0;
                      const label = stock > 0 ? `${brand.name} (${stock} in stock)` : `${brand.name} (0 in stock)`;
                      return (
                        <option key={brand.id} value={brand.name}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Expanded Fields */}
              {pond.expanded && (
                <div className="space-y-3 border-t border-gray-100 pt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Feed Type
                    </label>
                    <input
                      type="text"
                      value={pond.feed_type}
                      onChange={(e) => updatePond(pond.id, 'feed_type', e.target.value)}
                      placeholder="e.g., Starter, Grower, Finisher"
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-700">
                        Remarks (Optional)
                      </label>
                      <button
                        onClick={() => handleVoiceInput(pond.id)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <span className="text-sm">🎤</span>
                      </button>
                    </div>
                    <textarea
                      value={pond.remarks}
                      onChange={(e) => updatePond(pond.id, 'remarks', e.target.value)}
                      placeholder="Feed quality, fish response, etc."
                      rows={2}
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          ))
        )}
      </div>

      {/* Summary & Submit */}
      <div className="p-4 bg-white border-t border-gray-200 sticky bottom-0">
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <div className="text-xs text-blue-600 font-medium">Total Feed Quantity</div>
            <div className="text-lg font-bold text-blue-700">{getTotalQuantity()} kg</div>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading || creating}
          className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading || creating ? <LoadingSpinner size="sm" /> : <span className="text-lg">💾</span>}
          Submit EOD Feed ({getFilledPondsCount()}/{pondsData.length})
        </button>
      </div>
    </div>
  );
};

export default FeedForm;